import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import HorizontalScroll from "./HorizontalScroll";
import RowsSkeleton from "./RowsSkeleton";
import styles from "../../styles/MoodPicker.module.css";
import {
  getHistory, recordPlay, getMoods, saveMoods, addMoods, loadIndex, tasteProfile, recommend, recommendForMoods,
  toCard, trackReco,
} from "../lib/personalize";

const MoodPicker = dynamic(() => import("./MoodPicker"));

const isMobileUA = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const POPUP_DELAY_MS = 2000;
function fromSearchEngine() {
  try {
    return /(^|\.)(google|bing|yahoo|duckduckgo|yandex|baidu|ecosia)\./i.test(new URL(document.referrer).hostname);
  } catch {
    return false; // no referrer (direct visit)
  }
}

// Personalised rows, rendered only in the browser (search engines see the normal static page).
//   mode="home": mood picker popup for first-time visitors, then "Recently Played" (the visitor's history) + "Recommended For You"
//   mode="game": only records the play (the "Because you played" row under the game is static)
export default function PersonalRows({ mode, game }) {
  const [rows, setRows] = useState([]);
  const [picking, setPicking] = useState(false);
  const [version, setVersion] = useState(0); // bumped when the visitor saves new mood picks
  // rows still loading, shown as placeholders (RowsSkeleton): both until the first effect runs
  const [pending, setPending] = useState(["compact", "single"]);

  useEffect(() => {
    let cancelled = false;
    const mobile = isMobileUA();
    // Game pages: just remember the play. Their "Because you played …" row is built at build time
    // (similarGames in src/lib/gameData.js) so search engines see those links too.
    if (mode === "game") {
      recordPlay(game);
      return;
    }
    const history = getHistory();
    const saved = getMoods(); // null = never asked
    const picks = saved ? saved.ids : [];
    const played = history.map((h) => h.s);
    const built = [];

    // First visit on the homepage: ask for moods (popup) before recommending anything.
    // Wait a moment so the page shows first, and don't pop over a page someone just opened from search.
    if (mode === "home" && !history.length && !saved) {
      setRows([]);
      setPending([]);
      if (fromSearchEngine()) return;
      const timer = setTimeout(() => setPicking(true), POPUP_DELAY_MS);
      return () => clearTimeout(timer);
    }
    setPicking(false);

    if (mode === "home" && history.length) {
      built.push({ id: "continue_playing", title: "Recently Played", games: history.slice(0, 20).map(toCard), layout: "compact", more: "/recently-played" });
    }
    setRows([...built]);
    setPending(["single"]);

    // History plus mood picks (picks matter less once there is real play history)
    const profile = addMoods(tasteProfile(history), picks, history.length ? 0.5 : 1);
    if (!Object.keys(profile).length) { setPending([]); return; }

    loadIndex().then((index) => {
      if (cancelled) return;
      setPending([]);
      if (!index) return;
      // Games removed from the site may still be in a visitor's saved history: drop them from Continue Playing
      const live = new Set(index.games.map((g) => g.s));
      const cont = built.find((r) => r.id === "continue_playing");
      if (cont) {
        cont.games = cont.games.filter((c) => live.has(c.slug));
        if (!cont.games.length) built.splice(built.indexOf(cont), 1);
      }
      const games = mode === "home" && !history.length
        ? recommendForMoods(index, picks, { exclude: played, mobile })
        : recommend(index, profile, { exclude: played, mobile });
      if (!games.length) { setRows([...built]); return; }
      if (history.length) built.push({ id: "recommended_for_you", title: "Recommended For You", games });
      else built.push({ id: "picked_for_you", title: "Picked For You", games, canChange: true });
      setRows([...built]);
    });
    return () => { cancelled = true; };
  }, [mode, game && game.name[1], version]);

  const tracked = useRef(new Set());
  useEffect(() => {
    rows.forEach((row) => {
      if (tracked.current.has(row.id)) return;
      tracked.current.add(row.id);
      trackReco("personalized_row_view", { row: row.id, games_shown: row.games.length });
    });
  }, [rows]);

  // Closing on the first visit saves "no picks" so it isn't asked again; closing a reopened picker keeps the picks
  const picker = picking && (
    <MoodPicker
      key="mood-picker"
      initial={(getMoods() || { ids: [] }).ids}
      onDone={(ids) => { saveMoods(ids); setPicking(false); setVersion((v) => v + 1); }}
      onClose={() => {
        setPicking(false);
        if (!getMoods()) { saveMoods([]); setVersion((v) => v + 1); }
      }}
    />
  );

  const skeleton = mode === "home" ? pending.map((kind) => <RowsSkeleton key={`skeleton-${kind}`} kind={kind} />) : [];

  return [picker, ...rows.map((row) => (
    <HorizontalScroll
      key={row.id}
      games={row.games}
      category={row.title}
      viewMoreUrl={row.more}
      layoutType={row.layout || "single"}
      headerExtra={row.canChange && (
        <button type="button" className={styles.change} onClick={() => { trackReco("mood_picker_reopen", {}); setPicking(true); }}>
          Change my picks
        </button>
      )}
      onGameClick={(card, position) =>
        trackReco("personalized_click", { row: row.id, game: card.slug, position: position + 1 })}
    />
  )), ...skeleton];
}
